/**
 * DEFECTS TAB
 *
 * Linked-bug analytics for the current scope.
 *
 * Sections:
 *   1. Three metric cards — total bugs, affected scenarios, avg bugs/scenario.
 *   2. Top scenarios by bug count (project + release scope only; scenario scope
 *      has just the one scenario so the table would be trivial).
 *   3. Recent bug-link feed — latest 100 link events with scenario + timestamp.
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import { token } from '@atlaskit/tokens'

import MetricCardGrid from '../cards/MetricCardGrid'
import {
  fetchDefectsAsync,
  selectDefects,
  selectReportFilters,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'

const formatDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const SectionHeading = ({ title, subtitle }) => (
  <div style={{ marginBottom: '8px' }}>
    <div style={{ fontSize: '14px', fontWeight: 600, color: token('color.text', '#172B4D') }}>
      {title}
    </div>
    {subtitle && (
      <div style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C'), marginTop: '2px' }}>
        {subtitle}
      </div>
    )}
  </div>
)

const tableBase = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px'
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 500,
  color: token('color.text.subtle', '#6B778C'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const tdStyle = {
  padding: '10px 12px',
  color: token('color.text', '#172B4D'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const rowBg = (index) =>
  index % 2 === 0
    ? token('elevation.surface', '#FFFFFF')
    : token('color.background.neutral.subtle', '#FAFBFC')

const EmptyState = ({ label }) => (
  <div
    style={{
      padding: '32px',
      textAlign: 'center',
      color: token('color.text.subtle', '#6B778C'),
      fontSize: '13px',
      border: `1px dashed ${token('color.border', '#DFE1E6')}`,
      borderRadius: '4px'
    }}
  >
    {label}
  </div>
)

const DefectsTab = ({ level, id }) => {
  const dispatch = useDispatch()
  const defects = useSelector(selectDefects)
  const filters = useSelector(selectReportFilters)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  const filterKey = JSON.stringify(filters)

  useEffect(() => {
    if (id && level) {
      dispatch(fetchDefectsAsync({ level, id, filters }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, level, id, filterKey])

  if (loading.defects) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.defects) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load defect analytics</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.defects}</p>
      </div>
    )
  }

  if (!defects) {
    return <EmptyState label="No defect data." />
  }

  const { totalBugs = 0, uniqueScenariosWithBugs = 0, uniqueRunsWithBugs = 0, avgBugsPerScenario = 0, topScenarios = [], bugs = [] } = defects

  const metrics = [
    { value: totalBugs, label: 'Total Bugs' },
    { value: uniqueScenariosWithBugs, label: 'Affected Scenarios' },
    { value: uniqueRunsWithBugs, label: 'Affected Runs' },
    { value: Number(avgBugsPerScenario).toFixed(2), label: 'Avg Bugs / Scenario' }
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <MetricCardGrid metrics={metrics} compact gap="12px" />
      </section>

      {level !== 'scenario' && (
        <section>
          <SectionHeading
            title="Top scenarios by bug count"
            subtitle="The 10 scenarios where the most Jira bugs have been linked."
          />
          {topScenarios.length === 0 ? (
            <EmptyState label="No linked bugs in scope." />
          ) : (
            <div
              style={{
                border: `1px solid ${token('color.border', '#DFE1E6')}`,
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <table style={tableBase}>
                <thead>
                  <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                    <th style={thStyle}>Scenario</th>
                    <th style={thStyle}>Release</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Bugs</th>
                  </tr>
                </thead>
                <tbody>
                  {topScenarios.map((row, idx) => (
                    <tr key={row.scenarioId} style={{ backgroundColor: rowBg(idx) }}>
                      <td style={tdStyle}>
                        {row.scenarioKey && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginRight: '8px',
                              padding: '1px 6px',
                              fontSize: '11px',
                              fontWeight: 600,
                              borderRadius: '3px',
                              backgroundColor: token('color.background.neutral', '#F4F5F7'),
                              color: token('color.text.subtle', '#6B778C')
                            }}
                          >
                            {row.scenarioKey}
                          </span>
                        )}
                        {row.scenarioName}
                      </td>
                      <td style={{ ...tdStyle, color: token('color.text.subtle', '#6B778C') }}>
                        {row.releaseName || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                        {row.bugCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section>
        <SectionHeading
          title="Recent linked bugs"
          subtitle="Latest Jira bug links — click the key to open the issue in Jira."
        />
        {bugs.length === 0 ? (
          <EmptyState label="No bug links yet." />
        ) : (
          <div
            style={{
              border: `1px solid ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <table style={tableBase}>
              <thead>
                <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                  <th style={thStyle}>Bug</th>
                  <th style={thStyle}>Scenario</th>
                  <th style={thStyle}>Linked on</th>
                </tr>
              </thead>
              <tbody>
                {bugs.map((b, idx) => (
                  <tr key={b.id} style={{ backgroundColor: rowBg(idx) }}>
                    <td style={tdStyle}>
                      {/*
                        We don't know the Jira base URL here, so just render the
                        key as plain text. A future pass can resolve the base URL
                        via Jira Connect and make this clickable.
                      */}
                      <span style={{ fontWeight: 600 }}>{b.jiraIssueKey}</span>
                    </td>
                    <td style={tdStyle}>
                      {b.scenarioKey && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginRight: '8px',
                            padding: '1px 6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            borderRadius: '3px',
                            backgroundColor: token('color.background.neutral', '#F4F5F7'),
                            color: token('color.text.subtle', '#6B778C')
                          }}
                        >
                          {b.scenarioKey}
                        </span>
                      )}
                      {b.scenarioName || '—'}
                    </td>
                    <td style={{ ...tdStyle, color: token('color.text.subtle', '#6B778C') }}>
                      {formatDate(b.linkedOn)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default DefectsTab
